import { createContext, type FC, type ReactNode, useContext, useState } from "react";
import { TableDensityEnum } from "~enums/tableDensity.ts";
import { loadFromLocalStorage } from "~utilities/loadFromLocalStorage.ts";
import { saveToLocalStorage } from "~utilities/saveToLocalStorage.ts";

interface AppSettingsContextType {
  displayWidthConstrained: boolean;
  toggleConstrained: (value: boolean) => void;
  tableDensity: TableDensityEnum;
  toggleTableDensity: (value: TableDensityEnum) => void;
}

const AppSettingsContext = createContext<AppSettingsContextType | undefined>(undefined);

export const AppSettingsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [displayWidthConstrained, setDisplayWidthConstrained] = useState(() => {
    const saved = loadFromLocalStorage("display-width-constrained");
    return saved !== null ? !!saved : true;
  });

  const [tableDensity, setTableDensity] = useState(() => {
    const saved = loadFromLocalStorage("table-density");

    if (saved !== null && Object.values(TableDensityEnum).includes(saved)) {
      return saved;
    }

    return TableDensityEnum.DEFAULT;
  });

  const toggleConstrained = (value: boolean) => {
    setDisplayWidthConstrained(() => {
      saveToLocalStorage("display-width-constrained", JSON.stringify(value));
      return value;
    });
  };

  const toggleTableDensity = (value: TableDensityEnum) => {
    setTableDensity(() => {
      saveToLocalStorage("", JSON.stringify(value));
      return value;
    });
  };

  return (
    <AppSettingsContext.Provider
      value={{
        displayWidthConstrained,
        toggleConstrained,
        tableDensity,
        toggleTableDensity,
      }}
    >
      {children}
    </AppSettingsContext.Provider>
  );
};

export const useAppSettings = (): AppSettingsContextType => {
  const context = useContext(AppSettingsContext);
  if (!context) {
    throw new Error("useAppSettings must be used within a AppSettingsProvider");
  }
  return context;
};
